import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Repeat, Zap, Shield } from 'lucide-react';
import AdvancedConditionConfig from './AdvancedConditionConfig';

/**
 * Advanced Loop Configuration Component
 * 
 * Provides an intuitive UI for configuring loops with:
 * - For-each, while, and for-range loop types
 * - Parallel execution options
 * - Error handling per iteration
 * - Max iterations safety limit
 */
const AdvancedLoopConfig = ({ config, onChange }) => {
  const [loopType, setLoopType] = useState(config?.loopType || 'forEach');
  const [collection, setCollection] = useState(config?.collection || '');
  const [itemVariable, setItemVariable] = useState(config?.itemVariable || 'loop_item');
  const [indexVariable, setIndexVariable] = useState(config?.indexVariable || 'loop_index');
  const [maxIterations, setMaxIterations] = useState(config?.maxIterations || 100);
  const [parallel, setParallel] = useState(config?.parallel || false);
  const [parallelLimit, setParallelLimit] = useState(config?.parallelLimit || 5);
  const [continueOnError, setContinueOnError] = useState(config?.continueOnError !== false);
  const [condition, setCondition] = useState(config?.condition || null);
  const [start, setStart] = useState(config?.start || 0);
  const [end, setEnd] = useState(config?.end || 10);
  const [step, setStep] = useState(config?.step || 1);

  const updateConfig = (updates) => {
    onChange({
      ...config,
      ...updates
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold">Loop Configuration</h3>
        </div>
        <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          {loopType === 'forEach' ? 'For-Each' : loopType === 'while' ? 'While' : 'For-Range'}
        </Badge>
      </div>

      {/* Loop Type Selector */}
      <Card className="border-purple-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Loop Type</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={loopType} onValueChange={(value) => {
            setLoopType(value);
            updateConfig({ loopType: value });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="forEach">
                <div className="flex flex-col">
                  <span className="font-medium">For-Each Loop</span>
                  <span className="text-xs text-muted-foreground">Iterate over each item in a collection</span>
                </div>
              </SelectItem>
              <SelectItem value="while">
                <div className="flex flex-col">
                  <span className="font-medium">While Loop</span>
                  <span className="text-xs text-muted-foreground">Continue while a condition is true</span>
                </div>
              </SelectItem>
              <SelectItem value="forRange">
                <div className="flex flex-col">
                  <span className="font-medium">For-Range Loop</span>
                  <span className="text-xs text-muted-foreground">Iterate over a range of numbers</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* For-Each Configuration */}
      {loopType === 'forEach' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">For-Each Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Collection */}
            <div className="space-y-2">
              <Label>Collection (Array)</Label>
              <Input
                placeholder="{{previous_step.items}} or {{trigger.data}}"
                value={collection}
                onChange={(e) => {
                  setCollection(e.target.value);
                  updateConfig({ collection: e.target.value });
                }}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Reference an array from a previous step using {`{{step.variable}}`} syntax
              </p>
            </div>

            {/* Item Variable Name */}
            <div className="space-y-2">
              <Label>Item Variable Name</Label>
              <Input
                placeholder="loop_item"
                value={itemVariable}
                onChange={(e) => {
                  setItemVariable(e.target.value);
                  updateConfig({ itemVariable: e.target.value });
                }}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Access current item in loop body using {`{{${itemVariable}}}`}
              </p>
            </div>

            {/* Index Variable Name */}
            <div className="space-y-2">
              <Label>Index Variable Name</Label>
              <Input
                placeholder="loop_index"
                value={indexVariable}
                onChange={(e) => {
                  setIndexVariable(e.target.value);
                  updateConfig({ indexVariable: e.target.value });
                }}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Access current index (0, 1, 2...) using {`{{${indexVariable}}}`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* While Loop Configuration */}
      {loopType === 'while' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">While Loop Condition</CardTitle>
          </CardHeader>
          <CardContent>
            <AdvancedConditionConfig
              config={condition || {}}
              onChange={(newCondition) => {
                setCondition(newCondition);
                updateConfig({ condition: newCondition });
              }}
            />
            <p className="text-xs text-muted-foreground mt-3">
              The loop will continue as long as this condition is true
            </p>
          </CardContent>
        </Card>
      )}

      {/* For-Range Configuration */}
      {loopType === 'forRange' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">For-Range Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Start</Label>
                <Input
                  type="number"
                  value={start}
                  onChange={(e) => {
                    setStart(parseInt(e.target.value) || 0);
                    updateConfig({ start: parseInt(e.target.value) || 0 });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <Input
                  type="number"
                  value={end}
                  onChange={(e) => {
                    setEnd(parseInt(e.target.value) || 10);
                    updateConfig({ end: parseInt(e.target.value) || 10 });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Step</Label>
                <Input
                  type="number"
                  value={step}
                  onChange={(e) => {
                    setStep(parseInt(e.target.value) || 1);
                    updateConfig({ step: parseInt(e.target.value) || 1 });
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Loop from {start} to {end} with step {step} ({Math.max(0, Math.ceil((end - start) / step))} iterations)
            </p>
          </CardContent>
        </Card>
      )}

      {/* Performance Settings */}
      <Card className="border-blue-500/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-500" />
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Parallel Execution */}
          {loopType === 'forEach' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Parallel Execution</Label>
                  <p className="text-xs text-muted-foreground">
                    Process multiple items at the same time for faster execution
                  </p>
                </div>
                <Switch
                  checked={parallel}
                  onCheckedChange={(checked) => {
                    setParallel(checked);
                    updateConfig({ parallel: checked });
                  }}
                />
              </div>

              {parallel && (
                <div className="space-y-2 pl-4 border-l-2 border-blue-500">
                  <Label>Concurrent Limit: {parallelLimit}</Label>
                  <Slider
                    value={[parallelLimit]}
                    onValueChange={([value]) => {
                      setParallelLimit(value);
                      updateConfig({ parallelLimit: value });
                    }}
                    min={1}
                    max={20}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Process up to {parallelLimit} items simultaneously
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Max Iterations */}
          <div className="space-y-2">
            <Label>Max Iterations: {maxIterations}</Label>
            <Slider
              value={[maxIterations]}
              onValueChange={([value]) => {
                setMaxIterations(value);
                updateConfig({ maxIterations: value });
              }}
              min={1}
              max={1000}
              step={10}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Safety limit to prevent infinite loops
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Error Handling */}
      <Card className="border-orange-500/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-orange-500" />
            <CardTitle className="text-sm font-medium">Error Handling</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Continue on Error</Label>
              <p className="text-xs text-muted-foreground">
                If one iteration fails, continue with the next one
              </p>
            </div>
            <Switch
              checked={continueOnError}
              onCheckedChange={(checked) => {
                setContinueOnError(checked);
                updateConfig({ continueOnError: checked });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Loop Summary */}
      <Card className="bg-purple-500/5 border-purple-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Loop Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {loopType === 'forEach' && (
            <>
              <p>
                <span className="text-muted-foreground">Type:</span>{' '}
                <span className="font-medium">For-Each Loop</span>
              </p>
              <p>
                <span className="text-muted-foreground">Collection:</span>{' '}
                <span className="font-mono text-xs">{collection || '(not set)'}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Item Variable:</span>{' '}
                <span className="font-mono text-xs">{itemVariable}</span>
              </p>
              {parallel && (
                <p>
                  <span className="text-muted-foreground">Parallel:</span>{' '}
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                    {parallelLimit} concurrent
                  </Badge>
                </p>
              )}
            </>
          )}
          {loopType === 'while' && (
            <>
              <p>
                <span className="text-muted-foreground">Type:</span>{' '}
                <span className="font-medium">While Loop</span>
              </p>
              <p>
                <span className="text-muted-foreground">Condition:</span>{' '}
                <span className="text-xs">{condition?.conditions?.length || 0} condition(s)</span>
              </p>
            </>
          )}
          {loopType === 'forRange' && (
            <>
              <p>
                <span className="text-muted-foreground">Type:</span>{' '}
                <span className="font-medium">For-Range Loop</span>
              </p>
              <p>
                <span className="text-muted-foreground">Range:</span>{' '}
                <span className="font-mono text-xs">{start} to {end} step {step}</span>
              </p>
            </>
          )}
          <p>
            <span className="text-muted-foreground">Max Iterations:</span>{' '}
            <span className="font-medium">{maxIterations}</span>
          </p>
          <p>
            <span className="text-muted-foreground">Error Handling:</span>{' '}
            <Badge variant="outline" className={continueOnError ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
              {continueOnError ? 'Continue on error' : 'Stop on error'}
            </Badge>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedLoopConfig;
